import React, {useCallback} from 'react'
import {useSelector} from 'react-redux'
import {getAnonymousId} from 'modules/meta/selectors'

import {Box} from '@vio/atlas/helpers/Box'
import {Entity, Page} from '@vio/core/src/analytics/types/Events'
import {AuthenticationFlows} from '@vio/core/src/auth/types/Auth'
import {useLogIn} from '@vio/shared/src/authentication/hooks/useLogIn'

import {useHopperAd} from './hooks/useHopperAd'
import {useHopperAdEligibility} from './hooks/useHopperAdEligibility'
import {useHopperAdTracking} from './hooks/useHopperAdTracking'
import {HopperInlineBannerContent} from './HopperInlineBannerContent'

interface Props {
  disableMargins?: boolean
}

const HopperInlineBannerComponent = ({disableMargins}: Props) => {
  const {isEligible, placeId} = useHopperAdEligibility('Banner')
  const anonymousId = useSelector(getAnonymousId)
  const logIn = useLogIn()

  // Fetch ad from HTS API
  const {ads} = useHopperAd({
    variant: 'Banner',
    placeId,
    enabled: isEligible
  })
  const ad = ads[0] ?? null

  // Set up tracking (impression on view, click handler)
  const {impressionRef, trackClick} = useHopperAdTracking({
    ad,
    entity: Entity.HopperInlineAd,
    page: Page.Search
  })

  const handleCTAClick = useCallback(() => {
    // Track the click event
    trackClick()

    // Open SUSI modal for signup
    logIn({
      flow: AuthenticationFlows.SignUp,
      sourceComponent: 'HopperInlineBanner',
      anonymousId,
      analyticsData: {
        component: 'HopperInlineBanner',
        placeId
      }
    })
  }, [trackClick, logIn, placeId, anonymousId])

  if (!isEligible || !ad) return null

  return (
    <Box
      marginBottom={'s400'}
      desktopSm={{
        marginTop: disableMargins ? 's000' : 's600',
        marginBottom: disableMargins ? 's000' : 's600',
        marginLeft: 's200',
        marginRight: 's200'
      }}
    >
      <HopperInlineBannerContent
        ref={impressionRef}
        creative={ad.creative}
        onCTAClick={handleCTAClick}
      />
    </Box>
  )
}

export const HopperInlineBanner = React.memo(HopperInlineBannerComponent)
